import { Fragment, useCallback, useMemo, useState } from "react"
import { ArrowUpDown, Filter } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../Table/Table"
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/Popover"
import {
  TradesPagination,
  usePaginationState,
} from "../TradesPagination/TradesPagination"
import type { LogEntry, LogLevel } from "../types/logs"
import type { SortDirection } from "../TradesTable/TradesTable"

export interface LogsTableProps {
  logs: LogEntry[]
  levels?: LogLevel[]
  visibleLevels?: LogLevel[]
  defaultVisibleLevels?: LogLevel[]
  onVisibleLevelsChange?: (levels: LogLevel[]) => void
  sortDirection?: SortDirection
  defaultSortDirection?: SortDirection
  onSortDirectionChange?: (direction: SortDirection) => void
  defaultPageSize?: number
  levelLabels?: Partial<Record<LogLevel, string>>
  /** Controlled current page (0-indexed). When set, the table no longer slices `logs` and routes page changes to `onPageChange`. */
  page?: number
  /** Controlled page size. When set, the table uses this instead of its internal `defaultPageSize` state. */
  pageSize?: number
  /** Total row count across all pages (server-side pagination). Falls back to the filtered length when not set. */
  totalRows?: number
  /** Page-change handler. Required when `page` is controlled. */
  onPageChange?: (page: number) => void
  /** Page-size-change handler. When `pageSize` is controlled, the table calls this instead of its internal setter. */
  onPageSizeChange?: (size: number) => void
  className?: string
}

const LEVEL_STYLES: Record<LogLevel, string> = {
  info: "bg-primary/15 text-primary",
  warn: "bg-amber-500/15 text-amber-600",
  error: "bg-destructive/15 text-destructive",
  debug: "bg-muted text-muted-foreground",
}

const DATA_TOKEN = new RegExp(
  [
    "[A-Z]{2,}\\/[A-Z]{2,}(?:\\s+\\d+[smhdwMY])?",
    "[A-Z]{2,}\\([^)]+\\)",
    "SL\\s+\\d+(?:\\.\\d+)?%",
    "[+\\-]?\\d+(?:\\.\\d+)?%",
    "\\d{4}-\\d{2}-\\d{2}",
    "\\d{1,2}:\\d{2}(?::\\d{2})?(?:[\\u2013\\-]\\d{1,2}:\\d{2}(?::\\d{2})?)?",
    "\\b\\d+s\\b",
    "\\bv\\d+\\b",
    "\\b\\d{1,3}(?:,\\d{3})+\\b",
    "\\b\\d+\\/\\d+\\b",
  ].join("|"),
  "g",
)

function renderMessage(msg: string) {
  const parts: Array<{ t: "text" | "mono"; v: string }> = []
  let last = 0
  const re = new RegExp(DATA_TOKEN.source, "g")
  let match: RegExpExecArray | null
  while ((match = re.exec(msg)) !== null) {
    if (match.index > last)
      parts.push({ t: "text", v: msg.slice(last, match.index) })
    parts.push({ t: "mono", v: match[0] })
    last = match.index + match[0].length
  }
  if (last < msg.length) parts.push({ t: "text", v: msg.slice(last) })
  return parts.map((p, i) =>
    p.t === "text" ? (
      <Fragment key={i}>{p.v}</Fragment>
    ) : (
      <span key={i} className="font-mono">
        {p.v}
      </span>
    ),
  )
}

const ALL_LEVELS: LogLevel[] = ["info", "warn", "error"]

export function LogsTable({
  logs,
  levels = ALL_LEVELS,
  visibleLevels: controlledLevels,
  defaultVisibleLevels,
  onVisibleLevelsChange,
  sortDirection: controlledSort,
  defaultSortDirection = "desc",
  onSortDirectionChange,
  defaultPageSize = 10,
  levelLabels,
  page: controlledPage,
  pageSize: controlledPageSize,
  totalRows: controlledTotalRows,
  onPageChange,
  onPageSizeChange,
  className,
}: LogsTableProps) {
  const [internalLevels, setInternalLevels] = useState<Set<LogLevel>>(
    () => new Set(defaultVisibleLevels ?? levels),
  )
  const active = controlledLevels
    ? new Set(controlledLevels)
    : internalLevels

  const toggleLevel = useCallback(
    (level: LogLevel) => {
      const next = new Set(active)
      if (next.has(level)) {
        if (next.size > 1) next.delete(level)
      } else {
        next.add(level)
      }
      setInternalLevels(next)
      onVisibleLevelsChange?.([...next])
    },
    [active, onVisibleLevelsChange],
  )

  const [internalSort, setInternalSort] =
    useState<SortDirection>(defaultSortDirection)
  const sortDir = controlledSort ?? internalSort
  const toggleSort = useCallback(() => {
    const next: SortDirection = sortDir === "asc" ? "desc" : "asc"
    setInternalSort(next)
    onSortDirectionChange?.(next)
  }, [sortDir, onSortDirectionChange])

  const pagination = usePaginationState(controlledPageSize ?? defaultPageSize)

  const isPageControlled = controlledPage !== undefined
  const currentPage = isPageControlled ? controlledPage : pagination.page
  const setCurrentPage = useCallback(
    (next: number) => {
      if (isPageControlled) {
        onPageChange?.(next)
      } else {
        pagination.setPage(next)
        onPageChange?.(next)
      }
    },
    [isPageControlled, onPageChange, pagination],
  )

  const isPageSizeControlled = controlledPageSize !== undefined
  const effectivePageSize = isPageSizeControlled
    ? controlledPageSize
    : pagination.pageSize
  const setEffectivePageSize = useCallback(
    (next: number) => {
      if (isPageSizeControlled) {
        onPageSizeChange?.(next)
      } else {
        pagination.setPageSize(next)
        onPageSizeChange?.(next)
      }
    },
    [isPageSizeControlled, onPageSizeChange, pagination],
  )

  const filtered = useMemo(() => {
    const list = logs.filter((l) => active.has(l.level))
    return list.sort((a, b) => {
      const aKey = `${a.date} ${a.timestamp}`
      const bKey = `${b.date} ${b.timestamp}`
      return sortDir === "asc" ? aKey.localeCompare(bKey) : bKey.localeCompare(aKey)
    })
  }, [logs, active, sortDir])

  const totalRows = controlledTotalRows ?? filtered.length
  const pageCount = Math.max(1, Math.ceil(totalRows / effectivePageSize))
  const paged = isPageControlled
    ? filtered
    : filtered.slice(
        currentPage * effectivePageSize,
        (currentPage + 1) * effectivePageSize,
      )

  const HEAD = "text-caption text-muted-foreground uppercase tracking-wider"

  return (
    <div className={cn("flex flex-col h-full", className)}>
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={cn(HEAD, "w-28 sm:w-48")}>
                <button
                  type="button"
                  onClick={toggleSort}
                  className="text-caption inline-flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer uppercase"
                >
                  Date / Time
                  <ArrowUpDown size={10} className="text-foreground" />
                </button>
              </TableHead>
              <TableHead className={cn(HEAD, "w-16 sm:w-20")}>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-caption inline-flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer uppercase"
                    >
                      Status
                      <Filter
                        size={10}
                        className={cn(active.size < levels.length && "text-primary")}
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="v1 w-32 p-1" align="start">
                    {levels.map((level) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => toggleLevel(level)}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-caption cursor-pointer",
                          "hover:bg-muted/50 transition-colors",
                          active.has(level) ? "text-foreground" : "text-muted-foreground",
                        )}
                      >
                        <span
                          className={cn(
                            "w-3 h-3 rounded-sm border border-border flex items-center justify-center",
                            active.has(level) && "bg-primary border-primary",
                          )}
                        >
                          {active.has(level) && (
                            <svg
                              width="8"
                              height="8"
                              viewBox="0 0 8 8"
                              fill="none"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="1.5,4 3,5.5 6.5,2" />
                            </svg>
                          )}
                        </span>
                        {levelLabels?.[level] ?? level}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
              </TableHead>
              <TableHead className={HEAD}>
                <span className="text-caption uppercase tracking-wider">Message</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((log) => (
              <TableRow key={log.id} className="border-b border-border/40">
                <TableCell className="w-28 sm:w-48 text-caption text-muted-foreground font-mono align-top whitespace-normal">
                  <span className="block sm:inline">{log.date}</span>
                  <span className="block sm:inline sm:ml-1">{log.timestamp}</span>
                </TableCell>
                <TableCell className="w-16 sm:w-20 align-top">
                  <span
                    className={cn(
                      "inline-block px-1.5 py-0.5 rounded text-caption uppercase",
                      LEVEL_STYLES[log.level],
                    )}
                  >
                    {log.level}
                  </span>
                </TableCell>
                <TableCell className="text-body text-foreground whitespace-normal break-words align-top">
                  {renderMessage(log.message)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TradesPagination
        page={currentPage}
        pageSize={effectivePageSize}
        pageCount={pageCount}
        totalRows={totalRows}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setEffectivePageSize(size)
          setCurrentPage(0)
        }}
      />
    </div>
  )
}
